import React, { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import ContentWrapper from "../components/ContentWrapper"
import ContentHeader from "../components/ContentHeader"
import ContentMain from "../components/ContentMain"
import LoadingCard from "../components/LoadingCard"
import InteractiveMenuCard from "../components/InteractiveMenuCard"
import API from "../utils/API"

const DEFAULT_MENU_COUNT = 1
const MAX_MENU_COUNT = 20

export default function MainInteractiveMenu() {
  // State
  const [ads, setAds] = useState([])
  const [menus, setMenus] = useState([])
  const [totems, setTotems] = useState([])
  const [adsLoaded, setAdsLoaded] = useState(false)
  const [menusLoaded, setMenusLoaded] = useState(false)
  const [displayedMenuCount, setDisplayedMenuCount] = useState(DEFAULT_MENU_COUNT)

  // Comportements
  const getAds = () => {
    API.GET("/ad", (response) => {
      setAds(response.data.filter((ad) => [1, 2, 3].includes(ad.adTypeId)))
      setAdsLoaded(true)
    })
  }

  const getMenus = () => {
    API.GET("/interactive/menu", (response) => {
      setMenus(response.data)
      setMenusLoaded(true)
    })
  }

  const getTotems = () => {
    API.GET("/interactive/totem", (response) => {
      setTotems(response.data)
    })
  }

  useMemo(() => {
    getAds()
    getMenus()
    getTotems()
  }, [])

  // Un menu par défaut, puis ceux enregistrés (positions consécutives), plus celui ajouté avec le bouton +
  const savedMenuCount = Math.max(DEFAULT_MENU_COUNT, menus.length)
  const menuCount = Math.min(Math.max(savedMenuCount, displayedMenuCount), MAX_MENU_COUNT)
  const menuPositions = Array.from({ length: menuCount }, (_, index) => index + 1)
  // Un seul menu non enregistré à la fois, pour garder des positions consécutives
  const canAddMenu = menuCount < MAX_MENU_COUNT && menus.length >= menuCount

  const addMenu = () => setDisplayedMenuCount(menuCount + 1)

  // Retire le dernier menu s'il n'est pas enregistré
  const removeLastMenu = () => setDisplayedMenuCount(menuCount - 1)

  // Les menus suivants sont décalés d'une position par l'API
  const onMenuDeleted = () => {
    setDisplayedMenuCount(Math.max(menuCount - 1, DEFAULT_MENU_COUNT))
    getMenus()
  }

  // Rendu
  return (
    <ContentWrapper>
      <ContentHeader>
        <div className="col-sm-6">
          <h1 className="m-0">Menus interactifs</h1>
        </div>
        <div className="col-sm-6">
          <ol className="breadcrumb float-sm-right">
            <li className="breadcrumb-item">
              <Link to={"/"}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active">Menus interactifs</li>
          </ol>
        </div>
      </ContentHeader>
      <ContentMain>
        <div className="alert alert-info">
          <i className="icon fas fa-info" /> Configurez les menus affichés sur l'écran lorsqu'un passant le touche (jusqu'à 20 avec le bouton +).
          Chaque menu affiche un ou plusieurs documents (vidéos, images, PDF) de <Link to={"/ressources"}>vos ressources</Link>, dans l'ordre choisi.
          <br />
          {totems.length > 0 ? (
            <>
              Écran(s) concerné(s) : <b>{totems.map((totem) => totem.name).join(", ")}</b>
            </>
          ) : (
            <>Aucun écran n'est encore rattaché à votre équipe : contactez votre administrateur.</>
          )}
        </div>
        <LoadingCard loaded={adsLoaded && menusLoaded}>
          {ads.length === 0 && (
            <div className="alert alert-warning">
              <i className="icon fas fa-exclamation-triangle" /> Aucun média disponible.{" "}
              <Link to={"/ressources"}>Ajoutez des ressources</Link> pour configurer vos menus.
            </div>
          )}
          <div className="row">
            {menuPositions.map((position) => {
              const menu = menus.find((menu) => menu.position === position)
              const isRemovable = !menu && position === menuCount && position > savedMenuCount
              return (
                <div key={position} className="col-xl-4 col-lg-6">
                  <InteractiveMenuCard
                    position={position}
                    menu={menu}
                    ads={ads}
                    refreshMenus={getMenus}
                    onDeleted={onMenuDeleted}
                    removeMenu={isRemovable ? removeLastMenu : null}
                  />
                </div>
              )
            })}
            {canAddMenu && (
              <div className="col-xl-4 col-lg-6">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-block mb-3"
                  style={{ minHeight: "120px", borderStyle: "dashed" }}
                  onClick={addMenu}
                  title="Ajouter un menu"
                >
                  <i className="fas fa-plus fa-2x"></i>
                  <div>Ajouter un menu</div>
                </button>
              </div>
            )}
          </div>
        </LoadingCard>
      </ContentMain>
    </ContentWrapper>
  )
}
